import { useEffect, useRef, useState } from 'react'
import { useAiChat } from '../../context/AiChatContext'
import AiChatConversation from './AiChatConversation'
import { usePanneauDroit } from '../shell/cornerDock';

const MIN_WIDTH = 320
const MAX_WIDTH = 900
const DEFAULT_WIDTH = 384
const PANEL_TITLE_ID = 'ai-chat-panel-title'

function formatDate(isoString) {
  const date = new Date(isoString)
  const now = new Date()
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24))
  const time = date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
  if (diffDays === 0) return `Aujourd'hui à ${time}`
  if (diffDays === 1) return `Hier à ${time}`
  return `${date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })} à ${time}`
}

export default function AiChatPanel() {
  const {
    isOpen,
    availableContexts,
    selectedContext,
    currentConvId,
    history,
    toggle,
    selectContext,
    resetConversation,
    resumeConversation,
    deleteHistoryEntry,
  } = useAiChat()

  const [panelWidth, setPanelWidth] = useState(DEFAULT_WIDTH)

  // Décale la barre d'actions du coin : ouvert, ce panneau recouvre son bouton —
  // et sa largeur change au glissement de la poignée.
  usePanneauDroit('ai-chat', isOpen, panelWidth)
  const closeBtnRef = useRef(null)
  const isResizing = useRef(false)

  // Focus le bouton de fermeture après l'animation d'ouverture
  useEffect(() => {
    if (isOpen) {
      const t = setTimeout(() => closeBtnRef.current?.focus(), 310)
      return () => clearTimeout(t)
    }
  }, [isOpen])

  // Fermeture via Escape
  useEffect(() => {
    if (!isOpen) return
    const handleKey = (e) => {
      if (e.key === 'Escape') toggle()
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [isOpen, toggle])

  // Drag-to-resize depuis la poignée gauche
  const handleResizeStart = (e) => {
    e.preventDefault()
    isResizing.current = true
    const startX = e.clientX
    const startWidth = panelWidth

    const handleMouseMove = (e) => {
      if (!isResizing.current) return
      const delta = startX - e.clientX
      const newWidth = Math.min(Math.max(startWidth + delta, MIN_WIDTH), MAX_WIDTH)
      setPanelWidth(newWidth)
    }

    const handleMouseUp = () => {
      isResizing.current = false
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }

    document.body.style.cursor = 'ew-resize'
    document.body.style.userSelect = 'none'
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  return (
    <div
      role="dialog"
      aria-modal={isOpen}
      aria-labelledby={PANEL_TITLE_ID}
      aria-hidden={!isOpen}
      style={{ width: panelWidth }}
      className={`fixed top-0 right-0 h-full z-50 bg-white shadow-2xl flex flex-col border-l border-slate-200
        transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : 'translate-x-full'}
        ${!isOpen ? 'pointer-events-none' : ''}
      `}
    >
      {/* Poignée de redimensionnement */}
      <div
        className="absolute left-0 top-0 h-full w-2 cursor-ew-resize z-10 group flex items-center justify-center"
        onMouseDown={handleResizeStart}
        aria-hidden="true"
        title="Redimensionner le panel"
      >
        <div className="w-0.5 h-10 rounded-full bg-slate-300 group-hover:bg-iris-400 group-active:bg-iris-600 transition-colors" />
      </div>

      {/* Header */}
      <div className="bg-[image:var(--gradient-primary)] text-white px-4 py-3 flex items-center justify-between shrink-0">
        <span id={PANEL_TITLE_ID} className="font-semibold text-sm truncate pr-2">
          {selectedContext?.label ?? "Discuter avec l'IA"}
        </span>
        <div className="flex items-center gap-3 shrink-0">
          {selectedContext !== null && (
            <button
              onClick={resetConversation}
              aria-label="Nouvelle conversation"
              className="text-iris-200 hover:text-white text-xs underline underline-offset-2 transition-colors whitespace-nowrap"
            >
              Nouvelle conversation
            </button>
          )}
          <button
            ref={closeBtnRef}
            onClick={toggle}
            aria-label="Replier le panel de chat"
            className="text-iris-200 hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Body */}
      {availableContexts.length === 0 ? (
        <p className="p-4 text-slate-500 text-sm">
          Aucun contexte disponible sur cette page.
        </p>
      ) : !selectedContext ? (
        <div className="flex-1 overflow-y-auto">
          {/* Choix du contexte */}
          <div className="p-4 grid grid-cols-1 gap-3">
            {availableContexts.map((ctx) => (
              <button
                key={ctx.id}
                onClick={() => selectContext(ctx)}
                className="text-left border border-slate-200 rounded-lg p-4 hover:border-iris-400 hover:bg-iris-50 transition-colors"
              >
                <div className="font-semibold text-slate-900 text-sm">{ctx.label}</div>
                {ctx.description && (
                  <div className="text-slate-500 text-xs mt-1">{ctx.description}</div>
                )}
              </button>
            ))}
          </div>

          {/* Historique des conversations */}
          {history.length > 0 && (
            <div className="px-4 pb-4">
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                Conversations récentes
              </h3>
              <div className="grid grid-cols-1 gap-2">
                {history.map((entry) => {
                  const userMsgCount = (entry.messages ?? []).filter(m => m.role === 'user').length
                  return (
                    <div
                      key={entry.id}
                      className="group relative border border-slate-200 rounded-lg hover:border-iris-300 hover:bg-iris-50 transition-colors"
                    >
                      <button
                        onClick={() => resumeConversation(entry)}
                        className="w-full text-left p-3 pr-8"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="inline-block text-[10px] font-medium bg-iris-100 text-iris-700 rounded px-1.5 py-0.5 leading-tight">
                            {entry.contextLabel}
                          </span>
                        </div>
                        <p className="text-xs text-slate-700 font-medium truncate">
                          "{entry.firstQuestion}"
                        </p>
                        <p className="text-[11px] text-slate-400 mt-1">
                          {formatDate(entry.updatedAt)} · {userMsgCount} question{userMsgCount > 1 ? 's' : ''}
                        </p>
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteHistoryEntry(entry.id) }}
                        aria-label="Supprimer cette conversation de l'historique"
                        className="absolute top-2 right-2 p-1 text-slate-300 hover:text-rose-500 opacity-0 group-hover:opacity-100 transition-all"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      ) : (
        <AiChatConversation key={currentConvId} />
      )}
    </div>
  )
}
